import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { ReactComponent as Logo } from '../../../assets/images/logo.svg';
import { 
    Home, 
    MapPin, 
    Target, 
    Users, 
    Wrench, 
    Download, 
    User, 
    CreditCard, 
    Building2,
    LogOut,
    X,
    ChevronDown,
    Crosshair,
    Trees,
    CircleDot,
    Shield,
    Megaphone,
    UsersRound,
    Lock,
    BarChart3,
    ShoppingBag,
    Newspaper,
    PenSquare,
    Gift
} from 'lucide-react';
import { useUser } from '../../../UserContext';
import { hasPaidPlan } from '../../../utils/subscriptionStatus';
import { appConfig } from '../../config/appConfig';

const Sidebar = ({ toggleSidebar, closeSidebar, opened }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useUser();
    const [expandedMenus, setExpandedMenus] = useState({});

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const toggleSubmenu = (menuTitle) => {
        setExpandedMenus(prev => ({
            ...prev,
            [menuTitle]: !prev[menuTitle]
        }));
    };

    const scoreIcons = {
        target: Target,
        clay: CircleDot,
        rifle: Crosshair,
        game: Trees
    };

    const scoreSubmenu = appConfig.score.disciplines.map((discipline) => ({
        title: discipline.label,
        icon: scoreIcons[discipline.icon] || Target,
        path: discipline.path
    }));

    const sidebarItems = [
        { title: appConfig.navigation.dashboardLabel, icon: Home, path: '/dashboard/home', alwaysVisible: true },
        { title: appConfig.navigation.venuesLabel, icon: MapPin, path: '/dashboard/clubs', alwaysVisible: true },
        { 
            title: appConfig.navigation.scoreLabel,
            icon: Target, 
            path: '/dashboard/score',
            alwaysVisible: true,
            hasSubmenu: true,
            submenu: scoreSubmenu
        },
        { title: appConfig.navigation.analyticsLabel, icon: BarChart3, path: '/dashboard/analytics', alwaysVisible: true },
        { title: appConfig.navigation.marketplaceLabel, icon: ShoppingBag, path: '/dashboard/marketplace', alwaysVisible: true },
        { title: 'Connect Perks', icon: Gift, path: '/dashboard/connect-perks', alwaysVisible: true },
        { title: appConfig.navigation.contentLabel, icon: Newspaper, path: '/dashboard/blog', alwaysVisible: true },
        { title: appConfig.terminology.equipmentHubTitle, icon: Lock, path: '/dashboard/gun-cabinet', alwaysVisible: true },
        { title: appConfig.navigation.friendsLabel, icon: Users, path: '/dashboard/friends', alwaysVisible: true },
        { title: appConfig.navigation.equipmentLabel, icon: Wrench, path: '/dashboard/equipment', alwaysVisible: true },
        { title: appConfig.navigation.downloadLabel, icon: Download, path: '/dashboard/download', alwaysVisible: true },
        { title: appConfig.navigation.profileLabel, icon: User, path: '/dashboard/setting', alwaysVisible: true },
        { title: appConfig.navigation.subscriptionLabel, icon: CreditCard, path: '/dashboard/subscription', alwaysVisible: true },
        { title: appConfig.navigation.manageVenuesLabel, icon: Building2, path: '/dashboard/manage-clubs', requiresClubAdmin: true },
        { 
            title: 'Admin', 
            icon: Shield, 
            path: '/dashboard/admin',
            requiresAdmin: true,
            hasSubmenu: true,
            submenu: [
                { title: 'Dashboard', icon: Shield, path: '/dashboard/admin' },
                { title: 'Manage Banners', icon: Megaphone, path: '/dashboard/admin/banners' },
                { title: 'Manage Users', icon: UsersRound, path: '/dashboard/admin/users' },
                { title: 'Manage Blog', icon: PenSquare, path: '/dashboard/admin/blog' },
                { title: 'Equipment Data', icon: Wrench, path: '/dashboard/admin/equipment' },
                { title: 'Manage Perks', icon: Gift, path: '/dashboard/admin/perks' },
                { title: 'Manage Marketplace', icon: ShoppingBag, path: '/dashboard/admin/marketplace' },
            ]
        },
    ];

    const canSeeItem = (item) => {
        if (!user) return false;
        if (item.alwaysVisible) return true;
        if (item.requiresAdmin && user.isAdmin) return true;
        if (item.requiresSubscription && hasPaidPlan(user.subscription)) return true;
        if (item.requiresClubAdmin && (user.clubAdminCount > 0 || user.isAdmin)) return true;
        return false;
    };

    const isSubmenuActive = (submenu) => {
        return submenu.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
    };

    const handleNavSelection = () => {
        if (typeof closeSidebar === 'function') {
            closeSidebar();
            return;
        }

        toggleSidebar();
    };

    return (
        <>
            <div className={`sidebar-container ${opened ? 'active' : ''}`}>
                <div className="sidebar">
                    <div className="sidebar-header">
                        <Link to="/dashboard/home" className="sidebar-logo">
                            <Logo className="sidebar-logo-svg" />
                        </Link>
                        <button className="sidebar-close" onClick={toggleSidebar}>
                            <X size={24} />
                        </button>
                    </div>
                    
                    <nav className="sidebar-nav">
                        <ul className='sidebar-links'>
                            {sidebarItems.map((item, i) => (
                                canSeeItem(item) && (
                                    <li key={i} className={item.hasSubmenu ? 'has-submenu' : ''}>
                                        {item.hasSubmenu ? (
                                            <>
                                                <button
                                                    className={`sidebar-link sidebar-submenu-trigger ${
                                                        expandedMenus[item.title] || isSubmenuActive(item.submenu) ? 'active expanded' : ''
                                                    }`}
                                                    onClick={() => toggleSubmenu(item.title)}
                                                >
                                                    <span className="sidebar-link-icon">
                                                        <item.icon size={20} strokeWidth={2} />
                                                    </span>
                                                    <span className="sidebar-link-text">{item.title}</span>
                                                    <ChevronDown 
                                                        size={18} 
                                                        className={`submenu-arrow ${expandedMenus[item.title] ? 'expanded' : ''}`}
                                                    />
                                                </button>
                                                <ul className={`sidebar-submenu ${expandedMenus[item.title] || isSubmenuActive(item.submenu) ? 'expanded' : ''}`}>
                                                    {item.submenu.map((subItem, j) => (
                                                        <li key={j}>
                                                            <NavLink
                                                                to={subItem.path}
                                                                end={subItem.path === '/dashboard/score' || subItem.path === '/dashboard/admin'}
                                                                className={({ isActive }) => `sidebar-sublink ${isActive ? 'active' : ''}`}
                                                                onClick={handleNavSelection}
                                                            >
                                                                <span className="sidebar-sublink-icon">
                                                                    <subItem.icon size={16} strokeWidth={2} />
                                                                </span>
                                                                <span>{subItem.title}</span>
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            <NavLink
                                                to={item.path}
                                                state={item.path === '/dashboard/connect-perks' ? { fromDashboard: true, from: location.pathname } : undefined}
                                                end
                                                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                                                onClick={handleNavSelection}
                                            >
                                                <span className="sidebar-link-icon">
                                                    <item.icon size={20} strokeWidth={2} />
                                                </span>
                                                <span className="sidebar-link-text">{item.title}</span>
                                            </NavLink>
                                        )}
                                    </li>
                                )
                            ))}
                        </ul>
                    </nav>

                    <div className="sidebar-footer">
                        <button onClick={handleLogout} className="sidebar-logout">
                            <LogOut size={20} strokeWidth={2} />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </div>
            <div
                className={`sidebar-overlay ${opened ? 'active' : ''}`}
                onClick={handleNavSelection}
            ></div>
        </>
    );
};

export default Sidebar;
